import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-react';
import { cn } from 'src/lib/utils';
import { portalDs } from './portal-ds-classes';

type PortalDsAlertVariant = 'info' | 'success' | 'warning' | 'danger';

const VARIANT_CLASSES: Record<PortalDsAlertVariant, string> = {
  info: portalDs.alertInfo,
  success: portalDs.alertSuccess,
  warning: portalDs.alertWarning,
  danger: portalDs.alertDanger,
};

const VARIANT_ICONS: Record<PortalDsAlertVariant, typeof Info> = {
  info: Info,
  success: Check,
  warning: AlertTriangle,
  danger: AlertCircle,
};

interface PortalDsAlertProps {
  variant?: PortalDsAlertVariant;
  title?: string;
  children: React.ReactNode;
  onDismiss?: () => void;
  className?: string;
}

export function PortalDsAlert({
  variant = 'info',
  title,
  children,
  onDismiss,
  className,
}: PortalDsAlertProps) {
  const Icon = VARIANT_ICONS[variant];

  return (
    <div
      className={cn(portalDs.alertBase, VARIANT_CLASSES[variant], className)}
      role="alert"
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className={portalDs.alertTitle}>{title}</p> : null}
        <div className={portalDs.alertBody}>{children}</div>
      </div>
      {onDismiss ? (
        <button
          type="button"
          className="shrink-0 opacity-50 transition-opacity hover:opacity-100"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="size-4" />
        </button>
      ) : null}
    </div>
  );
}

interface PortalDsNoticeBarProps {
  children: React.ReactNode;
  className?: string;
}

export function PortalDsNoticeBar({ children, className }: PortalDsNoticeBarProps) {
  return (
    <div className={cn(portalDs.noticeBar, className)} role="status">
      <AlertTriangle className="size-[15px] shrink-0" aria-hidden />
      {children}
    </div>
  );
}
